import { useCallback, useSyncExternalStore } from 'react';

import { tokenStore } from '../api/token-store';

export interface ITableLayout {
  order: string[];
  widths: Record<string, number>;
}

const EMPTY: ITableLayout = { order: [], widths: {} };

export const MIN_COLUMN_PX = 56;
export const MAX_FIT_PX = 640;

// Per reader; the bare-id key predates that and still counts until replaced.
const storeKey = (id: string) => {
  const user = tokenStore.subject();
  return user ? `hp.table.${user}.${id}` : `hp.table.${id}`;
};

const parse = (raw: string | null): ITableLayout => {
  if (!raw) return EMPTY;
  try {
    const parsed = JSON.parse(raw) as Partial<ITableLayout>;
    return {
      order: Array.isArray(parsed.order) ? parsed.order.filter((k) => typeof k === 'string') : [],
      widths:
        parsed.widths && typeof parsed.widths === 'object'
          ? Object.fromEntries(
              Object.entries(parsed.widths).filter(([, v]) => typeof v === 'number' && v > 0),
            )
          : {},
    };
  } catch {
    // A layout is never worth a blank screen: fall back to the declared one.
    return EMPTY;
  }
};

const readStored = (id: string): string | null =>
  localStorage.getItem(storeKey(id)) ?? localStorage.getItem(`hp.table.${id}`);

const cache = new Map<string, { raw: string | null; layout: ITableLayout }>();
const listeners = new Set<() => void>();

const snapshot = (id: string | undefined): ITableLayout => {
  if (!id) return EMPTY;
  const raw = readStored(id);
  const hit = cache.get(id);
  if (hit && hit.raw === raw) return hit.layout;
  const layout = parse(raw);
  cache.set(id, { raw, layout });
  return layout;
};

const write = (id: string, next: ITableLayout | null) => {
  if (next === null) {
    localStorage.removeItem(storeKey(id));
    localStorage.removeItem(`hp.table.${id}`);
  } else {
    localStorage.setItem(storeKey(id), JSON.stringify(next));
  }
  for (const listener of listeners) listener();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const resetTableLayout = (id: string) => write(id, null);

export const isCustomLayout = (layout: ITableLayout) =>
  layout.order.length > 0 || Object.keys(layout.widths).length > 0;

export const useTableLayout = (id: string | undefined) => {
  const layout = useSyncExternalStore(subscribe, () => snapshot(id));

  const setWidth = useCallback(
    (key: string, px: number) => {
      if (!id) return;
      const current = snapshot(id);
      write(id, {
        ...current,
        widths: { ...current.widths, [key]: Math.max(MIN_COLUMN_PX, Math.round(px)) },
      });
    },
    [id],
  );

  const setOrder = useCallback(
    (order: string[]) => {
      if (id) write(id, { ...snapshot(id), order });
    },
    [id],
  );

  const reset = useCallback(() => {
    if (id) resetTableLayout(id);
  }, [id]);

  return { layout, setWidth, setOrder, reset };
};

// A column added after the reader last dragged this table still appears, where
// its author put it.
export const applyOrder = <T extends { key: string }>(
  columns: readonly T[],
  order: readonly string[],
): readonly T[] => {
  if (order.length === 0) return columns;

  const known = new Set(order);
  const byKey = new Map(columns.map((column) => [column.key, column]));
  const moved = order.map((key) => byKey.get(key)).filter((column): column is T => Boolean(column));

  const out = [...moved];
  columns.forEach((column, index) => {
    if (!known.has(column.key)) out.splice(Math.min(index, out.length), 0, column);
  });
  return out;
};
